import { Capacitor } from "@capacitor/core"
import { AppLauncher } from "@capacitor/app-launcher"

// --- Display labels ---

/**
 * Drop a trailing country from location display text so labels stay e.g. "Achterom 12, Breda".
 */
export function stripCountryFromLocationText(text: string): string {
  return text.replace(/,\s*(Nederland|Netherlands)\s*$/i, "").trimEnd()
}

/** Drop trailing city from address text for compact map labels (e.g. "Achterom 12, Breda" → "Achterom 12"). */
export function stripCityFromLocationText(text: string): string {
  const withoutCountry = stripCountryFromLocationText(text)
  const commaIndex = withoutCountry.indexOf(",")
  if (commaIndex === -1) return withoutCountry.trim()
  return withoutCountry.slice(0, commaIndex).trim()
}

/** Truncate map pin labels: max 15 chars, otherwise 13 chars + "..". */
export function truncateMapPinLabel(text: string): string {
  if (text.length <= 15) return text
  return `${text.slice(0, 13)}..`
}

/** Label above a map pin: prefer name, fall back to address; street only, no city. */
export function formatLocationMapPinLabel(
  location: { name?: string | null; address?: string | null } | null | undefined
): string | null {
  const name = location?.name?.trim()
  const address = location?.address?.trim()
  const raw = name || address
  if (!raw) return null
  return truncateMapPinLabel(stripCityFromLocationText(raw))
}

export const LOCATION_POINTER_HOVER_TITLE = "Dubbelklik om te bewerken"

// --- Maps-app navigation ---

function isIosUserAgent() {
  return /iPhone|iPad|iPod/i.test(navigator.userAgent)
}

function isAndroidUserAgent() {
  return /Android/i.test(navigator.userAgent)
}

/** Build a maps-app URL that opens navigation directly (no app chooser). */
export function buildMapsNavigationUrl(
  lat: number | null | undefined,
  lng: number | null | undefined,
  address?: string | null
): string | null {
  const destination = lat != null && lng != null ? `${lat},${lng}` : address?.trim()
  if (!destination) return null

  const platform = Capacitor.getPlatform()
  const encoded = encodeURIComponent(destination)
  if (platform === "ios" || (platform === "web" && isIosUserAgent())) {
    return `maps://?daddr=${lat != null && lng != null ? destination : encoded}`
  }
  if (platform === "android" || (platform === "web" && isAndroidUserAgent())) {
    return `google.navigation:q=${lat != null && lng != null ? destination : encoded}`
  }
  return `https://www.google.com/maps/dir/?api=1&destination=${encoded}`
}

export function openMapsNavigation(url: string | null | undefined) {
  if (!url) return

  if (Capacitor.isNativePlatform()) {
    void AppLauncher.openUrl({ url })
  } else {
    window.location.assign(url)
  }
}

// --- Google map pin ---

/** Yellow Google Maps pin (PNTS-2870 styling), optionally captioned with the location name. */
export function createGoogleLocationPinContent(
  PinElement: typeof google.maps.marker.PinElement,
  testId?: string,
  label?: string | null,
  pinColors?: { background: string; borderColor: string; glyphColor: string },
  hoverTitle?: string | null
): HTMLElement {
  const container = document.createElement("div")
  container.setAttribute("aria-label", label || hoverTitle || "Locatiepin")
  if (hoverTitle) container.title = hoverTitle
  container.style.display = "flex"
  container.style.flexDirection = "column"
  container.style.alignItems = "center"

  if (label) {
    const caption = document.createElement("div")
    caption.textContent = label
    caption.setAttribute("data-testid", "map-pin-label")
    caption.className =
      "surface mb-0.5 max-w-[180px] truncate rounded-md border-2 border-on-surface px-2 py-1 text-sm font-semibold whitespace-nowrap select-none"
    container.append(caption)
  }

  // Keep the test-id on the 44×44 pin itself so it stays the drag/click target.
  const pinBox = document.createElement("div")
  if (testId) {
    pinBox.setAttribute("data-testid", testId)
  }
  pinBox.style.width = "44px"
  pinBox.style.height = "44px"
  pinBox.style.display = "flex"
  pinBox.style.alignItems = "center"
  pinBox.style.justifyContent = "center"

  const pin = new PinElement({
    background: pinColors?.background ?? "#facc15",
    borderColor: pinColors?.borderColor ?? "#000000",
    glyphColor: pinColors?.glyphColor ?? "#111827"
  })
  pin.style.filter = "drop-shadow(0 0 1px #000000)"
  pinBox.append(pin)
  container.append(pinBox)

  return container
}

// --- Google Geocoding API ---

interface GeocodeAddressComponent {
  long_name: string
  short_name: string
  types: string[]
}

interface GeocodeResult {
  address_components: GeocodeAddressComponent[]
  formatted_address?: string
}

/** Build a Dutch-style street + city label from a Google Geocoding result. */
export function formatAddressFromGeocodeResult(result: GeocodeResult): string | null {
  const street = result.address_components.find((component) => component.types.includes("route"))
  const number = result.address_components.find((component) =>
    component.types.includes("street_number")
  )
  const city = result.address_components.find(
    (component) => component.types.includes("locality") || component.types.includes("postal_town")
  )

  if (!street || !city) {
    return result.formatted_address ?? null
  }

  return `${street.long_name}${number?.long_name ? ` ${number.long_name}` : ""}, ${city.long_name}`
}

async function fetchGeocode(params: Record<string, string>, apiKey: string) {
  const url = new URL("https://maps.googleapis.com/maps/api/geocode/json")
  for (const [key, value] of Object.entries(params)) url.searchParams.set(key, value)
  url.searchParams.set("key", apiKey)
  url.searchParams.set("language", "nl")

  const response = await fetch(url)
  if (!response.ok) return null

  const data = (await response.json()) as { status: string; results?: GeocodeResult[] }
  if (data.status !== "OK" || !data.results?.length) return null
  return data.results
}

/** Reverse geocode coordinates to the nearest street address via Google Geocoding API. */
export async function reverseGeocode(
  lat: number,
  lng: number,
  apiKey: string
): Promise<string | null> {
  if (!apiKey) return null

  const results = await fetchGeocode({ latlng: `${lat},${lng}` }, apiKey)
  if (!results) return null

  for (const result of results) {
    const formatted = formatAddressFromGeocodeResult(result)
    if (formatted) return formatted
  }

  return results[0]?.formatted_address ?? null
}

/** Geocode a street address to coordinates via Google Geocoding API. */
export async function forwardGeocode(
  address: string,
  apiKey: string
): Promise<{ lat: number; lng: number } | null> {
  if (!apiKey || !address.trim()) return null

  const results = (await fetchGeocode({ address, region: "nl" }, apiKey)) as
    { geometry?: { location?: { lat: number; lng: number } } }[] | null
  const location = results?.[0]?.geometry?.location
  return location ? { lat: location.lat, lng: location.lng } : null
}
